// Interactive project hour bars for ProjectBreakdown (roving listbox).
import { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { getProjectColor } from '@/utils/colorUtils';
import { formatHours } from '@/components/ui/formatHours';
import { useRovingBarGroup } from '@/features/dashboard/hooks/useRovingBarGroup';
import {
  DASHBOARD_SECTION_SX,
  DASHBOARD_SECTION_TITLE_SX,
  DASHBOARD_ROVING_LISTBOX_SX,
  rovingBarOptionSx,
} from './dashboardSectionStyles';

const DIMMED_OPACITY = 0.4;
const CHART_HEIGHT_PX = 120;

/**
 * @param {object} props
 * @param {{ name: string, hours: number }[]} props.sorted
 * @param {number} props.total
 * @param {string | null | undefined} props.selectedProject
 * @param {(name: string) => void} props.onProjectClick
 * @param {string} props.sectionLabel
 * @param {string} props.barsLabel
 */
export function ProjectBreakdownBars({
  sorted,
  total,
  selectedProject,
  onProjectClick,
  sectionLabel,
  barsLabel,
}) {
  const activateProject = useCallback(
    (index) => {
      const project = sorted[index];
      if (project?.name) onProjectClick(project.name);
    },
    [sorted, onProjectClick],
  );

  const [hasFocus, setHasFocus] = useState(false);

  const { activeIndex, setActiveIndex, onKeyDown } = useRovingBarGroup(
    sorted.length,
    activateProject,
  );

  return (
    <Box
      component="section"
      aria-label={sectionLabel}
      data-testid="dashboard-project-breakdown"
      sx={DASHBOARD_SECTION_SX}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={DASHBOARD_SECTION_TITLE_SX}
      >
        {sectionLabel}
      </Typography>
      <Box
        role="listbox"
        tabIndex={0}
        aria-label={barsLabel}
        aria-activedescendant={
          sorted[activeIndex]
            ? `dashboard-project-bar-${sorted[activeIndex].name}`
            : undefined
        }
        data-testid="dashboard-project-breakdown-bars"
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
        onKeyDown={onKeyDown}
        sx={{
          display: 'flex',
          gap: 1.5,
          height: CHART_HEIGHT_PX,
          ...DASHBOARD_ROVING_LISTBOX_SX,
        }}
      >
        {sorted.map((p, index) => {
          const isActive = index === activeIndex;
          return (
            <Box
              key={p.name}
              id={`dashboard-project-bar-${p.name}`}
              role="option"
              aria-selected={isActive}
              onClick={() => {
                setActiveIndex(index);
                onProjectClick(p.name);
              }}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                height: '100%',
                cursor: 'pointer',
                borderRadius: 0.5,
                ...rovingBarOptionSx(isActive, hasFocus),
                opacity:
                  selectedProject && selectedProject !== p.name
                    ? DIMMED_OPACITY
                    : 1,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  borderRadius: '3px 3px 0 0',
                  bgcolor: getProjectColor(p.projectId ?? p.name, p.colorIndex),
                  height: `${(p.hours / total) * CHART_HEIGHT_PX}px`,
                  outline: selectedProject === p.name ? '2px solid' : 'none',
                  outlineColor: 'text.primary',
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {p.name}
              </Typography>
              <Typography variant="caption" color="text.subtle">
                {formatHours(p.hours)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
